/**
 * Edit systems item component
 */

// React
import React, { FC } from 'react';

// Libraries
import { Controller, useWatch } from 'react-hook-form';

// Material
import { Box, useTheme } from '@mui/material';

// Components
import AppDate from 'components/AppDate';
import AppRadio from 'components/AppRadio';
import { AppCheckbox } from 'components/AppCheckbox';

// Common styles
import {
  SystemIcon,
  WrapperIcon,
  ContentSystem,
  TypographySystem,
  ContentSystemItem,
  RadioGroupContainer,
} from 'styles';

// Utils
import { defaultRadioIsUsed, formatDate } from 'utils/helper/common';
import { AWS_S3, STATUS_SYSTEM_TEXT, StatusKey } from 'utils/constants';

// Interface
interface EditSystemItemProps {
  control: any;
  errors: any;
  handleUsageChange: (_value: number) => void;
}

// Component
const EditSystemItem: FC<EditSystemItemProps> = ({ control, errors, handleUsageChange }) => {
  const theme = useTheme();
  const field = useWatch({ control });
  const isApproved = useWatch({ control, name: 'isApproved' });

  const checkStatus = (status: string): boolean => {
    return status === StatusKey.REQUESTED || status === StatusKey.IN_USED;
  };

  const defaultCheckDate = (status: string): number => {
    if (field?.is_used !== null) {
      return field.is_used;
    }
    return defaultRadioIsUsed(status);
  };

  return (
    <Box>
      <SystemIcon>
        <WrapperIcon
          src={`${AWS_S3.BASE_URL}/${field?.service_icon}`}
          alt="Image content system check"
        />
        <TypographySystem sx={{ fontWeight: 'bold', color: field.service_color }} variant="h4">
          {field.service_title}
        </TypographySystem>
      </SystemIcon>
      <Box display="flex">
        <Box
          sx={{
            width: '100px',
            [theme.breakpoints.down('sm')]: {
              width: '0px',
            },
          }}
        ></Box>
        <ContentSystem>
          <ContentSystemItem>
            <TypographySystem>現状ステータス</TypographySystem>
            <TypographySystem>{STATUS_SYSTEM_TEXT[field?.status]}</TypographySystem>
          </ContentSystemItem>
          <ContentSystemItem>
            <TypographySystem>利用 ON/OFF</TypographySystem>
            <Controller
              name="is_used"
              control={control}
              render={({ field: radioField }) => (
                <RadioGroupContainer
                  {...radioField}
                  value={
                    radioField.value === null ? defaultRadioIsUsed(field?.status) : radioField.value
                  }
                  onChange={(e) => handleUsageChange(Number(e.target.value))}
                >
                  <AppRadio disabled={!checkStatus(field?.status)} value={1} label={'ON'} />
                  <AppRadio disabled={!checkStatus(field?.status)} value={0} label={'OFF'} />
                </RadioGroupContainer>
              )}
            />
          </ContentSystemItem>
          <ContentSystemItem>
            <TypographySystem>非承認</TypographySystem>
            <Controller
              name="isApproved"
              control={control}
              render={({ field: checkboxField }) => (
                <AppCheckbox {...checkboxField} checked={checkboxField.value} />
              )}
            />
          </ContentSystemItem>
          <ContentSystemItem>
            <TypographySystem>申込日</TypographySystem>
            <TypographySystem>{formatDate(field?.created_at)}</TypographySystem>
          </ContentSystemItem>
          <ContentSystemItem>
            <TypographySystem>開始日</TypographySystem>
            <AppDate
              control={control}
              name="start_date"
              disabled={defaultCheckDate(field?.status) === 0 || isApproved}
              helperText={errors?.systems?.start_date?.message}
            />
          </ContentSystemItem>
          <ContentSystemItem>
            <TypographySystem>終了日</TypographySystem>

            <AppDate
              name="end_date"
              control={control}
              disabled={defaultCheckDate(field?.status) === 0 || isApproved}
              helperText={errors?.systems?.end_date?.message}
            />
          </ContentSystemItem>
        </ContentSystem>
      </Box>
    </Box>
  );
};

export default EditSystemItem;
